import {
  Badge,
  Heading,
  Paragraph,
  Sheet,
  SheetContent,
  PageLoader,
} from '@/components/atoms';
import { useWhatsAppTemplate } from '@/api';
import { TemplatePreview } from './template-preview';
import { TemplateStatusBadge } from './template-status-badge';
import type { WhatsAppTemplate } from '@/types/whatsapp-template';

interface TemplateDetailSheetProps {
  template: WhatsAppTemplate | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const TemplateDetailSheet = ({
  template,
  open,
  onOpenChange,
}: TemplateDetailSheetProps) => {
  const { data: detail, isLoading } = useWhatsAppTemplate(
    open && template ? template.id : undefined
  );

  const item = detail ?? template;

  if (!item) return null;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-2xl overflow-y-auto pt-10 pb-6 px-4 md:px-6">
        <div className="flex flex-col gap-6">
          <div className="flex flex-col gap-2">
            <Heading size="xl">{item.title}</Heading>
            <Paragraph size="sm" className="font-mono text-muted-foreground">
              {item.name}
            </Paragraph>
            <div className="flex flex-wrap gap-2 mt-1">
              <TemplateStatusBadge status={item.status} />
              {item.isAiCompatible ? (
                <Badge variant="info">AI compatible</Badge>
              ) : null}
            </div>
          </div>

          {isLoading ? (
            <PageLoader message="Loading template…" className="py-10" />
          ) : (
            <>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-muted-foreground">Category</p>
                  <p className="font-medium">{item.category}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Language</p>
                  <p className="font-medium uppercase">{item.language}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Sent</p>
                  <p className="font-medium">{item.sentCount}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Delivery rate</p>
                  <p className="font-medium">{item.deliveryRate}%</p>
                </div>
              </div>

              {item.internalNote ? (
                <div className="rounded-lg border border-accent bg-muted/20 p-3">
                  <p className="text-xs font-semibold text-muted-foreground mb-1">
                    Internal note
                  </p>
                  <p className="text-sm">{item.internalNote}</p>
                </div>
              ) : null}

              {item.rejectionReason ? (
                <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-3">
                  <p className="text-xs font-semibold text-destructive mb-1">
                    Rejection reason
                  </p>
                  <p className="text-sm">{item.rejectionReason}</p>
                </div>
              ) : null}

              <TemplatePreview
                header={item.header}
                body={item.body}
                footer={item.footer}
                buttons={item.buttons}
                variables={item.variables}
              />
            </>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
};
